// status-progress-journey-steps.ts
//
// Progress panel P1: the per-step builders behind `deriveJourney`
// (status-progress-journey-derivation.ts — split to keep both files under the
// 200-line budget): gates.json → gate steps, ticket groups → survey/plan/
// delivering (rule B3), review candidates + release labels → integration,
// deploy tickets + release labels → release. Pure.
import { CONTROLLER_RESERVED_TICKET_IDS } from "./controller-reserved-ticket-ids.js";
import type { GateFile, GateId } from "./gate-registry.js";
import type { RecoveryTicketTrigger } from "./recovery-ticket-shape.js";
import type { StatusLabelEffect } from "./status-progress-status-label-classes.js";
import type { JourneyState, JourneyStep, JourneyStepId } from "./status-progress-types.js";

export interface JourneyTicket {
  id: string;
  kind: string;
  slice_id: string;
  status: string;
  physical_runs: number;
  started_at?: string;
  finished_at?: string;
}

/** A recovery pair the orchestrator ledger journaled AND the ticket file confirms (Y2). */
export interface JourneyRecovery {
  source_ticket: string;
  trigger: RecoveryTicketTrigger;
  recovery_ticket: string;
}

export interface JourneyInput {
  gates: GateFile;
  tickets: readonly JourneyTicket[];
  /** Journal ticket statuses — the review candidates T-9001/T-9002 are never ticket files. */
  journalTicketStatus: Readonly<Record<string, string>>;
  recoveries: readonly JourneyRecovery[];
  statusLabel?: string;
  /** B17: the label is a stop/wait label that later work outlived ⇒ no overlay. */
  labelStale: boolean;
  releaseConfigured: boolean;
}

export const CLOSED_TICKET_STATUSES: ReadonlySet<string> = new Set([
  "done",
  "cancelled",
  "superseded",
]);
const LIVE = new Set(["todo", "in_progress"]);
export const STOPPED = new Set(["failed", "blocked"]);
export const PROGRESSED: ReadonlySet<JourneyState> = new Set([
  "done",
  "active",
  "waiting_human",
  "blocked",
]);
export const GATE_IDS: ReadonlySet<string> = new Set(["g0", "g1", "g2", "g3"]);

export function gateStep(gates: GateFile, id: GateId): JourneyStep {
  const g = gates[id];
  const source = `gate:${id}`;
  const at = g?.at;
  if (g?.status === "approved" || g?.status === "waived") {
    return { id, state: "done", ...(at === undefined ? {} : { ended_at: at }), source };
  }
  if (g?.status === "pending") {
    return { id, state: "waiting_human", ...(at === undefined ? {} : { started_at: at }), source };
  }
  if (g?.status === "invalidated")
    return { id, state: "waiting_human", summary: "cần duyệt lại", source };
  return { id, state: "pending", source };
}

const earliest = (xs: (string | undefined)[]) => xs.filter((x) => x !== undefined).sort()[0];
const latest = (xs: (string | undefined)[]) =>
  xs
    .filter((x) => x !== undefined)
    .sort()
    .at(-1);

/** Recovery pairs whose recovery ticket is still todo/in_progress — the loop is unblocking the source. */
export function liveRecoveries(
  input: Pick<JourneyInput, "tickets" | "journalTicketStatus" | "recoveries">,
): JourneyRecovery[] {
  const statusOf = (id: string) =>
    input.tickets.find((t) => t.id === id)?.status ?? input.journalTicketStatus[id];
  return input.recoveries.filter((r) => LIVE.has(statusOf(r.recovery_ticket) ?? ""));
}

/** B3 ticket-group rule; `recovering` = blocked sources a live recovery ticket is unblocking. */
export function groupStep(
  id: JourneyStepId,
  tickets: readonly JourneyTicket[],
  recovering: ReadonlySet<string>,
): JourneyStep {
  const source = `tickets:${id}`;
  // Empty ⇒ pending here; `skipEmptyPassedSteps` turns it into skipped once a later step moved.
  if (tickets.length === 0) return { id, state: "pending", source };
  const started = earliest(tickets.map((t) => t.started_at));
  const times = started === undefined ? {} : { started_at: started };
  const has = (pred: (t: JourneyTicket) => boolean) => tickets.some(pred);
  if (tickets.every((t) => CLOSED_TICKET_STATUSES.has(t.status))) {
    const ended = latest(tickets.map((t) => t.finished_at));
    return {
      id,
      state: "done",
      ...times,
      ...(ended === undefined ? {} : { ended_at: ended }),
      source,
    };
  }
  let state: JourneyState = "pending";
  if (has((t) => t.status === "in_progress")) state = "active";
  else if (has((t) => STOPPED.has(t.status) && !recovering.has(t.id))) state = "blocked";
  else if (has((t) => t.status === "done") || tickets.some((t) => t.physical_runs > 0)) {
    state = "active";
  }
  return { id, state, ...times, source };
}

/**
 * DF-P4-1 / p5d M-1: the controller closes the review only when BOTH reserved
 * candidates settled (supervisor-candidate-review-phase.ts), but the group only
 * holds rows the journal has seen. One closed row ⇒ `active` "1/2 review xong",
 * never `done` — except at a terminal mission (nothing more will run).
 */
export function reviewStep(
  tickets: readonly JourneyTicket[],
  recovering: ReadonlySet<string>,
  terminal: boolean,
): JourneyStep {
  const step = groupStep("review", tickets, recovering);
  const expected = CONTROLLER_RESERVED_TICKET_IDS.length;
  const closed = tickets.filter((t) => CLOSED_TICKET_STATUSES.has(t.status)).length;
  if (terminal || closed === 0 || closed >= expected) return step;
  const { ended_at: _ended, ...open } = step;
  return {
    ...open,
    state: step.state === "done" ? "active" : step.state,
    summary: `${closed}/${expected} review xong`,
  };
}

/** "slice k/n · x/y ticket xong" — the `recovery` slice is not a plan slice (X7). */
export function deliveringSummary(tickets: readonly JourneyTicket[]): string | undefined {
  if (tickets.length === 0) return undefined;
  const slices = [...new Set(tickets.map((t) => t.slice_id))].filter((s) => s !== "recovery");
  const open = slices.findIndex((s) =>
    tickets.some((t) => t.slice_id === s && !CLOSED_TICKET_STATUSES.has(t.status)),
  );
  const counted = tickets.filter((t) => t.status !== "cancelled" && t.status !== "superseded");
  const done = counted.filter((t) => t.status === "done").length;
  const tally = `${done}/${counted.length} ticket xong`;
  if (slices.length === 0) return tally;
  return `slice ${open < 0 ? slices.length : open + 1}/${slices.length} · ${tally}`;
}

export function integrationStep(input: JourneyInput, effect: StatusLabelEffect): JourneyStep {
  const id = "integration";
  if (!input.releaseConfigured) return { id, state: "skipped", source: "config:release" };
  const review = CONTROLLER_RESERVED_TICKET_IDS.map((t) => input.journalTicketStatus[t]);
  if (review.includes("in_progress")) return { id, state: "active", source: "tickets:review" };
  if (review.some((s) => s !== undefined && STOPPED.has(s))) {
    return { id, state: "blocked", source: "tickets:review" };
  }
  const label = `label:${input.statusLabel}`;
  if (effect.kind === "integration") return { id, state: effect.state, source: label };
  if (effect.kind === "integration_done" || effect.kind === "release") {
    return { id, state: "done", source: label };
  }
  const g3 = input.gates.g3?.status;
  if (g3 !== undefined && g3 !== "not_requested") return { id, state: "done", source: "gate:g3" };
  return { id, state: "pending", source: "tickets:review" };
}

export function releaseStep(
  input: JourneyInput,
  effect: StatusLabelEffect,
  deploys: readonly JourneyTicket[],
): JourneyStep {
  const id = "release";
  if (!input.releaseConfigured) return { id, state: "skipped", source: "config:release" };
  if (deploys.some((t) => t.status === "in_progress")) {
    return { id, state: "active", source: "tickets:deploy" };
  }
  if (effect.kind === "release") {
    const summary = effect.summary === undefined ? {} : { summary: effect.summary };
    return { id, state: effect.state, ...summary, source: `label:${input.statusLabel}` };
  }
  return { id, state: "pending", source: "gate:g3" };
}
